import { useCallback, useMemo } from 'react';
import { useGroupRules, UserGroupRules } from 'src/graphql/entities/group-rules';
import { useCurrentUserProfile } from 'src/graphql/entities/platform-user';
import { useCrossPartnerUserSettingsContext } from 'src/graphql/entities/side-panel-personalization/context';
import {
  BUILTIN_DEFAULT_PROFILE_ID,
  makeBuiltinDefaultProfile,
} from 'src/graphql/entities/side-panel-personalization/model/builtin-default';
import {
  filterAllowedProperties,
  SidePanelPropertyDescriptor,
  SidePanelSurfaceConfig,
} from 'src/graphql/entities/side-panel-personalization/model/descriptors';
import {
  buildLayout,
  reconcileLayout,
} from 'src/graphql/entities/side-panel-personalization/model/layout';
import {
  SidePanelLayout,
  SidePanelPersonalizationState,
  SidePanelProfile,
  SidePanelPropertyKey,
  SidePanelSectionAvailability,
  SidePanelSectionKey,
} from 'src/graphql/entities/side-panel-personalization/model/layout.types';
import {
  parsePersonalizationState,
  parseUserProfiles,
} from 'src/graphql/entities/side-panel-personalization/model/profiles';
import { resolveEffectiveLayout } from 'src/graphql/entities/side-panel-personalization/model/resolve';
import { useGlobalStore } from 'src/store';
import { useMyGroupProfiles } from './use-my-group-profiles';

const JSON_VALUE_TYPE = 'string';

export interface SidePanelPersonalizationSection {
  key: SidePanelSectionKey;
  label: string;
  loading: boolean;
  properties: { descriptor: SidePanelPropertyDescriptor; visible: boolean }[];
  notice?: string;
  hidePanelLabel?: boolean;
  reorderDisabled?: boolean;
}

export interface SidePanelPersonalizationActions {
  applyProfile: (profileId: string) => void;
  updateDraft: (next: SidePanelLayout) => void;
  saveAsProfile: (name: string) => void;
  deleteProfile: (profileId: string) => void;
  resetToProfile: () => void;
}

export interface SidePanelPersonalizationViewModel {
  hideEmpty: boolean;
  isDraft: boolean;
  /** true until the personalization inputs (user settings, rules, group profiles) resolve */
  loading: boolean;
  activeProfile: SidePanelProfile | null;
  profiles: SidePanelProfile[];
  sections: SidePanelPersonalizationSection[];
  effectiveLayout: SidePanelLayout;
  actions: SidePanelPersonalizationActions;
}

export function useSidePanelPersonalization(
  surface: SidePanelSurfaceConfig,
): SidePanelPersonalizationViewModel {
  const { user } = useCurrentUserProfile();
  const { checkValidity, loading: rulesLoading } = useGroupRules();
  const selectedPartnerId = useGlobalStore.use.selectedPartnerId();
  const canPersonalize = checkValidity([UserGroupRules.AccessToPersonalizeSidepanel]);

  const {
    values: crossPartnerValues,
    setValue: setCrossPartnerValue,
    loading: settingsLoading,
  } = useCrossPartnerUserSettingsContext();

  const profilesRaw = crossPartnerValues[surface.settingIds.profiles];

  const userProfiles = useMemo(() => parseUserProfiles(profilesRaw), [profilesRaw]);

  const setUserProfiles = useCallback(
    (next: SidePanelProfile[]) =>
      setCrossPartnerValue(surface.settingIds.profiles, JSON.stringify(next), JSON_VALUE_TYPE),
    [setCrossPartnerValue, surface.settingIds.profiles],
  );

  const stateRaw = crossPartnerValues[surface.settingIds.personalization];

  const state = useMemo(() => parsePersonalizationState(stateRaw), [stateRaw]);

  const setState = useCallback(
    (next: SidePanelPersonalizationState | null) =>
      setCrossPartnerValue(
        surface.settingIds.personalization,
        JSON.stringify(next),
        JSON_VALUE_TYPE,
      ),
    [setCrossPartnerValue, surface.settingIds.personalization],
  );

  const { groupProfiles, loading: groupProfilesLoading } = useMyGroupProfiles(
    surface.settingIds.groupProfile,
  );

  const rawSectionData = surface.useSections();

  // drop fields whose allowedRules the selected partner doesn't satisfy, once
  // here, so they're absent everywhere downstream — modal, reconciled layout,
  // builtin default
  const sectionData = useMemo(
    () => filterAllowedProperties(rawSectionData, selectedPartnerId, checkValidity),
    [rawSectionData, selectedPartnerId, checkValidity],
  );

  const availability = useMemo<SidePanelSectionAvailability[]>(
    () =>
      sectionData.map(({ key, properties }) => ({
        key,
        availableKeys: properties.map((descriptor) => descriptor.key),
        defaultVisibleKeys: properties
          .filter((descriptor) => descriptor.defaultVisible)
          .map((descriptor) => descriptor.key),
        groupKeyByProperty: Object.fromEntries(
          properties
            .filter((descriptor) => descriptor.group)
            .map((descriptor) => [descriptor.key, descriptor.group!.key]),
        ),
      })),
    [sectionData],
  );

  const builtinDefault = useMemo(
    () => makeBuiltinDefaultProfile(availability),
    [availability],
  );

  const resolved = useMemo(
    () =>
      resolveEffectiveLayout({
        canPersonalize,
        state,
        userProfiles,
        groupProfiles,
        builtinDefault,
      }),
    [canPersonalize, state, userProfiles, groupProfiles, builtinDefault],
  );

  const reconciled = useMemo(
    () => reconcileLayout(resolved.layout, availability),
    [resolved.layout, availability],
  );

  const effectiveLayout = useMemo(
    () => buildLayout(reconciled, resolved.layout.hideEmpty),
    [reconciled, resolved.layout.hideEmpty],
  );

  const sections = useMemo<SidePanelPersonalizationSection[]>(() => {
    const descriptorsByKey = new Map<SidePanelPropertyKey, SidePanelPropertyDescriptor>(
      sectionData.flatMap((section) =>
        section.properties.map((descriptor) => [descriptor.key, descriptor]),
      ),
    );

    return reconciled.map((section, index) => {
      const source = sectionData[index];

      return {
        key: section.key,
        label: source.label,
        loading: source.loading,
        notice: source.notice,
        hidePanelLabel: source.hidePanelLabel,
        // order only has a consumer when the panel renders the rows itself or
        // the card opts in (renders its fields in layout order); otherwise the
        // section ignores order, so disable reordering
        reorderDisabled:
          !source.reorderable && source.properties.every((descriptor) => !descriptor.Row),
        properties: section.properties.flatMap(({ key, visible }) => {
          const descriptor = descriptorsByKey.get(key);
          return descriptor ? [{ descriptor, visible }] : [];
        }),
      };
    });
  }, [reconciled, sectionData]);

  const profiles = useMemo(
    () => [
      builtinDefault,
      ...[...groupProfiles].sort((a, b) => a.name.localeCompare(b.name)),
      ...userProfiles,
    ],
    [builtinDefault, groupProfiles, userProfiles],
  );

  const activeProfileId = resolved.activeProfile?.id ?? null;

  const applyProfile = useCallback(
    (profileId: string) => setState({ activeProfileId: profileId, draft: null }),
    [setState],
  );

  const updateDraft = useCallback(
    (next: SidePanelLayout) => setState({ activeProfileId, draft: next }),
    [setState, activeProfileId],
  );

  const saveAsProfile = useCallback(
    (name: string) => {
      const profile: SidePanelProfile = {
        id: crypto.randomUUID(),
        name,
        source: 'user',
        layout: effectiveLayout,
      };
      setUserProfiles([...userProfiles, profile]);
      setState({ activeProfileId: profile.id, draft: null });
    },
    [effectiveLayout, setUserProfiles, userProfiles, setState],
  );

  const deleteProfile = useCallback(
    (profileId: string) => {
      setUserProfiles(userProfiles.filter((profile) => profile.id !== profileId));
      if (state?.activeProfileId === profileId) {
        setState({ activeProfileId: BUILTIN_DEFAULT_PROFILE_ID, draft: state.draft });
      }
    },
    [setUserProfiles, userProfiles, state, setState],
  );

  const resetToProfile = useCallback(
    () => setState({ activeProfileId, draft: null }),
    [setState, activeProfileId],
  );

  const actions = useMemo<SidePanelPersonalizationActions>(
    () => ({ applyProfile, updateDraft, saveAsProfile, deleteProfile, resetToProfile }),
    [applyProfile, updateDraft, saveAsProfile, deleteProfile, resetToProfile],
  );

  // until these resolve the layout would silently fall back to the builtin
  // default and flash the wrong rows on cold loads
  const loading = !user || rulesLoading || groupProfilesLoading || settingsLoading;

  // referentially stable — this object is a context value once provided
  return useMemo(
    () => ({
      hideEmpty: resolved.layout.hideEmpty,
      isDraft: resolved.isDraft,
      loading,
      activeProfile: resolved.activeProfile,
      profiles,
      sections,
      effectiveLayout,
      actions,
    }),
    [resolved, loading, profiles, sections, effectiveLayout, actions],
  );
}
